import { useRef } from "react";
import { useLocation } from "react-router-dom";

import type { AgenticLogicSurface as AgenticLogicServingSurface } from "@intelligems/ig-types/dtos/agentic-logic/surface";

import {
  type AgenticLogicSurface,
  embeddedAgenticLogicSurface,
  resolveAgenticLogicView,
} from "@src/features/ai/pages/agentic-logic/surface/surface";
import {
  EMBEDDED_RULE_PARAM,
  EMBEDDED_RULE_TAB_PARAM,
  EMBEDDED_SESSION_PARAM,
} from "@src/features/ai/pages/agentic-logic/surface/deriveEmbeddedPaths";

/**
 * The surface for a rule builder embedded in a host — an upsell's settings,
 * the onsite builder.
 *
 * Every path it offers is derived from the HOST's own location, so the host's
 * query string (the experience, its tab, a component id) rides along on the
 * rule views and is restored exactly on exit. The host location is the current
 * one with the rule params stripped: taking the live location as-is would fold
 * each view's params into the base for the next one.
 */
export const useEmbeddedAgenticLogicSurface = ({
  title,
  backLabel,
  placementSurface,
}: {
  title: string;
  backLabel: string;
  placementSurface: AgenticLogicServingSurface;
}): AgenticLogicSurface => {
  const { pathname, search } = useLocation();
  const host = new URLSearchParams(search);
  host.delete(EMBEDDED_RULE_PARAM);
  host.delete(EMBEDDED_RULE_TAB_PARAM);
  host.delete(EMBEDDED_SESSION_PARAM);
  const hostSearch = host.toString();

  // Kept across renders so the descriptor the provider registers is stable
  // while only the rule params move.
  const cache = useRef<AgenticLogicSurface | null>(null);
  const key = `${pathname}?${hostSearch}|${title}|${backLabel}|${placementSurface}`;
  const keyRef = useRef<string | null>(null);
  if (keyRef.current !== key || cache.current === null) {
    keyRef.current = key;
    cache.current = embeddedAgenticLogicSurface({
      hostPathname: pathname,
      hostSearch,
      title,
      backLabel,
      placementSurface,
    });
  }
  return cache.current;
};

/** The view an embedded surface's host is currently showing. */
export const useEmbeddedAgenticLogicView = () =>
  resolveAgenticLogicView(
    useLocation().search,
    EMBEDDED_RULE_PARAM,
    EMBEDDED_SESSION_PARAM
  );
